/**
 * Canlı kanal istemcisi (TI-012/TI-013; ADR-0019). Sekme başına TEK `EventSource` (`/api/live`); haber akışı paneli ve bildirim zili
 * aynı kanala abone olur; gelen kutusu konuşma olaylarını (`conversation`, G1) aynı kanaldan alır. Kanal yalnız ipucu taşır; içerik yetkili uçlardan okunur.
 * - Kopma: tarayıcı kendisi yeniden bağlanır ve `Last-Event-ID` gönderir (sunucu kaçırılanı bir kez gönderir). Bağlantı kalıcı
 *   kapanırsa (oturum süresi doldu, 401) oturum sessizce yenilenir (`/auth/me` → apiFetch yenileme yolu) ve kanal `?since=` ile
 *   yeniden kurulur. Her yeniden açılışta abonelere `resync` gider: panel kendi imleciyle telafi okur (kimlikle tekilleştirir).
 * - Yedek: `EventSource` yoksa veya art arda açılamazsa kısa yoklamaya düşülür (`LIVE_FALLBACK_POLL_SECONDS`, yalnız sekme
 *   görünürken); bir süre sonra canlı kanal yeniden denenir. Yoklama aboneye aynı `resync` olayı olarak gelir.
 */
import {
  LIVE_FALLBACK_POLL_SECONDS,
  liveConversationHintSchema,
  liveFeedHintSchema,
  type LiveConversationHint,
  type LiveFeedHint,
} from '@castinflow/shared';
import { apiFetch } from '../../lib/api';

export type LiveStatus = 'connecting' | 'live' | 'reconnecting' | 'polling' | 'off';

export type LiveMessage =
  | { type: 'feed'; hint: LiveFeedHint }
  | { type: 'feed-update'; ids: string[] }
  | { type: 'notification' }
  /** G1: görünür konuşmada değişiklik (yalnız kimlik + tür; içerik yetkili uçtan okunur) */
  | { type: 'conversation'; items: LiveConversationHint['items'] }
  /** Kopma sonrası yeniden açıldı ya da yedek yoklama zamanı: abone kendi imleciyle telafi okur */
  | { type: 'resync' };

type Listener = (m: LiveMessage) => void;
type StatusListener = (s: LiveStatus) => void;

/** Art arda bu kadar açılamazsa yedek yoklamaya geçilir */
const MAX_FAILURES = 3;
/** Yedek yoklamada canlı kanalın yeniden denenmesi */
const RETRY_LIVE_MS = 120_000;

export interface LiveChannelOptions {
  url?: string;
  /** Test: EventSource yerine geçen kurucu */
  eventSource?: (url: string) => EventSource;
  /** Oturum yenileme (varsayılan: `/auth/me` — apiFetch 401'de sessizce yeniler) */
  refreshSession?: () => Promise<void>;
  pollMs?: number;
  isVisible?: () => boolean;
}

export class LiveChannel {
  private es: EventSource | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private status: LiveStatus = 'off';
  private lastEventId: string | null = null;
  private opened = false;
  private failures = 0;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private readonly url: string;
  private readonly custom: boolean;
  private readonly make: (url: string) => EventSource;
  private readonly refresh: () => Promise<void>;
  private readonly pollMs: number;
  private readonly visible: () => boolean;

  constructor(opts: LiveChannelOptions = {}) {
    this.url = opts.url ?? '/api/live';
    this.custom = opts.eventSource !== undefined;
    this.make = opts.eventSource ?? ((u) => new EventSource(u, { withCredentials: true }));
    this.refresh =
      opts.refreshSession ??
      (async () => {
        await apiFetch<unknown>('/auth/me');
      });
    this.pollMs = opts.pollMs ?? LIVE_FALLBACK_POLL_SECONDS * 1000;
    this.visible =
      opts.isVisible ??
      (() => typeof document === 'undefined' || document.visibilityState !== 'hidden');
  }

  getStatus(): LiveStatus {
    return this.status;
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  }

  onStatus(l: StatusListener): () => void {
    this.statusListeners.add(l);
    return () => {
      this.statusListeners.delete(l);
    };
  }

  start(): void {
    this.closed = false;
    if (this.es !== null || this.pollTimer !== null) return;
    if (!this.custom && typeof EventSource === 'undefined') {
      this.startPolling();
      return;
    }
    this.connect();
  }

  stop(): void {
    this.closed = true;
    this.es?.close();
    this.es = null;
    if (this.pollTimer !== null) clearInterval(this.pollTimer);
    this.pollTimer = null;
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.setStatus('off');
  }

  private setStatus(s: LiveStatus) {
    if (this.status === s) return;
    this.status = s;
    for (const l of this.statusListeners) l(s);
  }

  private emit(m: LiveMessage) {
    for (const l of [...this.listeners]) l(m);
  }

  private connect() {
    if (this.closed) return;
    let es: EventSource;
    try {
      const since =
        this.lastEventId === null ? '' : `?since=${encodeURIComponent(this.lastEventId)}`;
      es = this.make(`${this.url}${since}`);
    } catch {
      this.startPolling();
      return;
    }
    this.es = es;
    this.setStatus(this.opened ? 'reconnecting' : 'connecting');
    const track = (e: MessageEvent) => {
      if (e.lastEventId !== '') this.lastEventId = e.lastEventId;
    };
    es.addEventListener('ready', () => {
      const again = this.opened;
      this.opened = true;
      this.failures = 0;
      this.setStatus('live');
      // Kopma sonrası: abone kendi imleciyle eksikleri okur (sunucu da Last-Event-ID ile telafi eder; tekilleştirme istemcide)
      if (again) this.emit({ type: 'resync' });
    });
    es.addEventListener('feed', (e) => {
      const m = e as MessageEvent<string>;
      track(m);
      const parsed = liveFeedHintSchema.safeParse(safeJson(m.data));
      if (parsed.success) this.emit({ type: 'feed', hint: parsed.data });
    });
    es.addEventListener('feed-update', (e) => {
      const m = e as MessageEvent<string>;
      track(m);
      const v = safeJson(m.data) as { ids?: unknown } | null;
      const ids = Array.isArray(v?.ids)
        ? v.ids.filter((x): x is string => typeof x === 'string')
        : [];
      if (ids.length > 0) this.emit({ type: 'feed-update', ids });
    });
    es.addEventListener('conversation', (e) => {
      const m = e as MessageEvent<string>;
      track(m);
      const parsed = liveConversationHintSchema.safeParse(safeJson(m.data));
      if (parsed.success) this.emit({ type: 'conversation', items: parsed.data.items });
    });
    es.addEventListener('notification', (e) => {
      track(e as MessageEvent<string>);
      this.emit({ type: 'notification' });
    });
    es.onerror = () => {
      if (this.closed) return;
      // CONNECTING: tarayıcı kendisi yeniden bağlanıyor (Last-Event-ID ile)
      if (es.readyState !== 2) {
        this.setStatus('reconnecting');
        return;
      }
      // CLOSED: kalıcı kapandı (ör. 401, oturum süresi doldu) → oturumu yenile, yeniden kur; art arda başarısızsa yedeğe geç
      es.close();
      if (this.es === es) this.es = null;
      this.failures += 1;
      if (this.failures >= MAX_FAILURES) {
        this.startPolling();
        return;
      }
      this.setStatus('reconnecting');
      void this.refresh()
        .catch(() => undefined)
        .then(() => {
          this.retryTimer = setTimeout(() => {
            this.retryTimer = null;
            this.connect();
          }, 1000 * this.failures);
        });
    };
  }

  private startPolling() {
    if (this.closed || this.pollTimer !== null) return;
    this.es?.close();
    this.es = null;
    this.setStatus('polling');
    this.pollTimer = setInterval(() => {
      if (!this.visible()) return;
      this.emit({ type: 'resync' });
      this.emit({ type: 'notification' });
    }, this.pollMs);
    // Bir süre sonra canlı kanal yeniden denenir
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.pollTimer !== null) clearInterval(this.pollTimer);
      this.pollTimer = null;
      this.failures = 0;
      if (this.custom || typeof EventSource !== 'undefined') this.connect();
      else this.startPolling();
    }, RETRY_LIVE_MS);
  }
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s) as unknown;
  } catch {
    return null;
  }
}
